// Hands a report's CSV to the native app so it can open the OS share sheet
// (save to Files, email, etc.). In a WebView an <a download>/blob can't save a
// file, so react-csv's normal download silently fails there.
//
// Returns true if a native bridge handled the export; false on plain web — the
// caller should then let the normal browser download proceed.
//   - Android: window.AndroidExport.exportCsv(csvString, filename)
//   - iOS:     window.webkit.messageHandlers.exportHandler.postMessage({ csv, filename })

function escapeCsvCell(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Serialize react-csv style inputs: headers = [{label, key}] (or plain strings),
// data = array of objects keyed by `key`.
function buildCsv(data, headers) {
  const cols = (headers || []).map((h) =>
    typeof h === "string" ? { label: h, key: h } : h
  );
  const headerLine = cols.map((c) => escapeCsvCell(c.label)).join(",");
  const rows = (data || []).map((row) =>
    cols.map((c) => escapeCsvCell(row?.[c.key])).join(",")
  );
  return [headerLine, ...rows].join("\r\n");
}

export function exportReportToNative(data, headers, filename) {
  const androidExport = window.AndroidExport;
  const iosHandler = window.webkit?.messageHandlers?.exportHandler;
  if (typeof androidExport?.exportCsv !== "function" && !iosHandler) {
    return false; // plain web — let react-csv download normally
  }

  const base = String(filename || "report");
  const name = base.toLowerCase().endsWith(".csv") ? base : `${base}.csv`;
  const csv = buildCsv(data, headers);

  if (typeof androidExport?.exportCsv === "function") {
    androidExport.exportCsv(csv, name);
  } else {
    iosHandler.postMessage({ csv, filename: name });
  }
  return true;
}

// Saves react-csv style data as a .csv without a <CSVLink>: for an Export
// button that fetches its data on click. Native bridge first, else a browser
// download.
export function downloadReportCsv(data, headers, filename) {
  if (exportReportToNative(data, headers, filename)) return;
  const base = String(filename || "report");
  const name = base.toLowerCase().endsWith(".csv") ? base : `${base}.csv`;
  const blob = new Blob([buildCsv(data, headers)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Hands a generated PDF (base64, no data: prefix) to the native app so it can
// save/share it. jsPDF's doc.save() builds a blob + <a download>, which silently
// does nothing inside a WebView — same problem the CSV bridge above solves.
//
// Returns true if a native bridge handled it; false on plain web (and on
// Android builds that don't expose exportPdf yet) so the caller falls back to
// doc.save() exactly as before.
// iOS uses its own pdfHandler rather than the CSV exportHandler on purpose: an
// app build that has not added PDF support yet simply has no pdfHandler, so we
// return false instead of posting a body the CSV handler would mis-handle.
//   - Android: window.AndroidExport.exportPdf(base64, filename)
//   - iOS:     window.webkit.messageHandlers.pdfHandler
//                .postMessage({ pdf: base64, filename })
// True when a PDF bridge is present, so callers can skip building a jsPDF doc
// they would not be able to deliver anyway.
export function hasNativePdfBridge() {
  return (
    typeof window.AndroidExport?.exportPdf === "function" ||
    Boolean(window.webkit?.messageHandlers?.pdfHandler)
  );
}

export function exportPdfToNative(base64, filename) {
  const androidExport = window.AndroidExport;
  const iosHandler = window.webkit?.messageHandlers?.pdfHandler;

  const base = String(filename || "document");
  const name = base.toLowerCase().endsWith(".pdf") ? base : `${base}.pdf`;
  const pdf = String(base64 || "");
  if (!pdf) return false;

  if (typeof androidExport?.exportPdf === "function") {
    androidExport.exportPdf(pdf, name);
    return true;
  }
  if (iosHandler) {
    iosHandler.postMessage({ pdf, filename: name });
    return true;
  }
  return false;
}

// Same as exportReportToNative but for callers that already built the CSV string
// themselves (manual `new Blob`/`<a download>` reports). Returns true if a native
// bridge handled it; false on plain web so the caller runs its normal blob download.
export function exportRawCsvToNative(csv, filename) {
  const androidExport = window.AndroidExport;
  const iosHandler = window.webkit?.messageHandlers?.exportHandler;
  if (typeof androidExport?.exportCsv !== "function" && !iosHandler) {
    return false; // plain web — let the normal browser download proceed
  }

  const base = String(filename || "report");
  const name = base.toLowerCase().endsWith(".csv") ? base : `${base}.csv`;
  const csvString = String(csv ?? "");

  if (typeof androidExport?.exportCsv === "function") {
    androidExport.exportCsv(csvString, name);
  } else {
    iosHandler.postMessage({ csv: csvString, filename: name });
  }
  return true;
}
